import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import NavBar from "../components/NavBar";
import api from "../api";

const SessionManagementPage = () => {
  const [sessions, setSessions] = useState([]);
  const [sessionStats, setSessionStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [modalSessionId, setModalSessionId] = useState("");
  const [actionReason, setActionReason] = useState("");

  const currentUser = useSelector((state) => state.auth.user);

  // Check if user is super admin
  const isSuperAdmin = currentUser?.role === "super_admin";

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error("Access denied. Super admin privileges required.");
      return;
    }
    fetchSessions();
    fetchSessionStats();
  }, [isSuperAdmin]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.getSessions();
      setSessions(response.data || []);
    } catch (error) {
      toast.error(`Failed to fetch sessions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionStats = async () => {
    try {
      const response = await api.getSessionStats();
      setSessionStats(response.data || {});
    } catch (error) {
      console.error("Failed to fetch session stats:", error);
    }
  };

  const handleSessionAction = async (sessionId, action, reason = "") => {
    try {
      let response;
      switch (action) {
        case "suspend":
          response = await api.suspendSession(sessionId, reason);
          break;
        case "resume":
          response = await api.resumeSession(sessionId);
          break;
        case "terminate":
          response = await api.terminateSession(sessionId, reason);
          break;
        default:
          throw new Error("Invalid action");
      }

      toast.success(response.message || `Session ${action}ed successfully`);
      fetchSessions();
      fetchSessionStats();
    } catch (error) {
      toast.error(`Failed to ${action} session: ${error.message}`);
    }
  };

  const handleBulkTerminate = async () => {
    if (selectedSessions.length === 0) {
      toast.warn("Please select sessions to terminate");
      return;
    }

    try {
      const response = await api.bulkTerminateSessions(
        selectedSessions,
        actionReason
      );
      toast.success(
        `${response.terminatedCount} sessions terminated successfully`
      );
      setSelectedSessions([]);
      setActionReason("");
      fetchSessions();
      fetchSessionStats();
    } catch (error) {
      toast.error(`Failed to terminate sessions: ${error.message}`);
    }
  };

  const handleCleanupExpired = async () => {
    try {
      const response = await api.cleanupSessions();
      toast.success(`${response.cleanedCount} expired sessions cleaned up`);
      fetchSessions();
      fetchSessionStats();
    } catch (error) {
      toast.error(`Failed to cleanup sessions: ${error.message}`);
    }
  };

  const openActionModal = (sessionId, action) => {
    setModalSessionId(sessionId);
    setModalAction(action);
    setActionReason("");
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (
      (modalAction === "suspend" || modalAction === "terminate") &&
      !actionReason.trim()
    ) {
      toast.warn("Please provide a reason for this action");
      return;
    }

    await handleSessionAction(modalSessionId, modalAction, actionReason);
    setShowModal(false);
  };

  const getStatusBadge = (session) => {
    const now = new Date();
    const expiresAt = new Date(session.expiresAt);

    if (session.terminatedAt) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
          Terminated
        </span>
      );
    }
    if (session.suspendedAt) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
          Suspended
        </span>
      );
    }
    if (expiresAt < now) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
          Expired
        </span>
      );
    }
    if (session.isActive) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
          Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
        Inactive
      </span>
    );
  };

  const filteredSessions = sessions.filter((session) => {
    const matchesSearch =
      session.adminId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.adminId?.email
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      session.ipAddress?.includes(searchTerm);

    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "active")
      return matchesSearch && session.isActive && !session.suspendedAt;
    if (filterStatus === "suspended")
      return matchesSearch && session.suspendedAt;
    if (filterStatus === "terminated")
      return matchesSearch && session.terminatedAt;
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (startDate, endDate = new Date()) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = end - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar currentPage={"/session-management"} />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Access Denied
            </h2>
            <p className="text-gray-600">
              Super admin privileges required to access session management.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar currentPage={"/session-management"} />
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Session Management
            </h1>
            <p className="mt-2 text-gray-600">
              Monitor and control all admin sessions in real-time
            </p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">
                Total Sessions
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                {sessionStats.totalSessions || 0}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">
                Active Sessions
              </h3>
              <p className="text-2xl font-bold text-green-600">
                {sessionStats.activeSessions || 0}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">
                Suspended Sessions
              </h3>
              <p className="text-2xl font-bold text-yellow-600">
                {sessionStats.suspendedSessions || 0}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500">
                Online Admins
              </h3>
              <p className="text-2xl font-bold text-blue-600">
                {sessionStats.uniqueAdmins || 0}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                {/* Search */}
                <div className="flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Search by admin name, email, or IP..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Filter */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Sessions</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => fetchSessions()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Refresh
                </button>
                <button
                  onClick={handleCleanupExpired}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cleanup Expired
                </button>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedSessions.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    {selectedSessions.length} session(s) selected
                  </span>
                  <input
                    type="text"
                    placeholder="Reason for termination..."
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    className="flex-1 px-3 py-1 border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    onClick={handleBulkTerminate}
                    className="px-4 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                  >
                    Terminate Selected
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sessions Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSessions(
                                filteredSessions.map((s) => s.sessionId)
                              );
                            } else {
                              setSelectedSessions([]);
                            }
                          }}
                          checked={
                            selectedSessions.length ===
                              filteredSessions.length &&
                            filteredSessions.length > 0
                          }
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Device
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Activity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSessions.map((session) => (
                      <tr key={session.sessionId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedSessions.includes(
                              session.sessionId
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSessions([
                                  ...selectedSessions,
                                  session.sessionId,
                                ]);
                              } else {
                                setSelectedSessions(
                                  selectedSessions.filter(
                                    (id) => id !== session.sessionId
                                  )
                                );
                              }
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {session.adminId?.name || "Unknown"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {session.adminId?.email || "Unknown"}
                              </div>
                              <div className="text-xs text-gray-400">
                                {session.adminId?.role || "Unknown"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(session)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{session.ipAddress}</div>
                          {session.location && (
                            <div className="text-xs text-gray-500">
                              {session.location.city},{" "}
                              {session.location.country}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {session.deviceInfo && (
                            <div>
                              <div>{session.deviceInfo.browser}</div>
                              <div className="text-xs text-gray-500">
                                {session.deviceInfo.os} -{" "}
                                {session.deviceInfo.device}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDuration(session.loginTime)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{formatDate(session.lastActivity)}</div>
                          <div className="text-xs text-gray-500">
                            {session.requestCount} requests
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            {session.isActive &&
                              !session.suspendedAt &&
                              !session.terminatedAt && (
                                <>
                                  <button
                                    onClick={() =>
                                      openActionModal(
                                        session.sessionId,
                                        "suspend"
                                      )
                                    }
                                    className="text-yellow-600 hover:text-yellow-900 text-xs px-2 py-1 border border-yellow-300 rounded"
                                  >
                                    Suspend
                                  </button>
                                  <button
                                    onClick={() =>
                                      openActionModal(
                                        session.sessionId,
                                        "terminate"
                                      )
                                    }
                                    className="text-red-600 hover:text-red-900 text-xs px-2 py-1 border border-red-300 rounded"
                                  >
                                    Terminate
                                  </button>
                                </>
                              )}
                            {session.suspendedAt && !session.terminatedAt && (
                              <button
                                onClick={() =>
                                  handleSessionAction(
                                    session.sessionId,
                                    "resume"
                                  )
                                }
                                className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-300 rounded"
                              >
                                Resume
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredSessions.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No sessions found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {modalAction.charAt(0).toUpperCase() + modalAction.slice(1)}{" "}
                Session
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to {modalAction} this session?
              </p>
              {(modalAction === "suspend" || modalAction === "terminate") && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Please provide a reason for this action..."
                  />
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className={`px-4 py-2 text-white rounded-md ${
                    modalAction === "terminate"
                      ? "bg-red-600 hover:bg-red-700"
                      : modalAction === "suspend"
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {modalAction.charAt(0).toUpperCase() + modalAction.slice(1)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionManagementPage;
